import { forwardRef } from "react";

/**
 * MiseOne Button — single source of truth (Prompt 16B).
 *
 * Variants: primary | secondary | ghost | danger
 * Sizes:    sm | md | lg
 * Use as a drop-in replacement for ad-hoc <button> elements.
 */

const VARIANT_CLASS = {
    primary:
        "bg-neutral-900 text-white hover:bg-neutral-800 active:bg-black " +
        "shadow-[0_1px_2px_rgb(0_0_0_/_0.06)]",
    secondary:
        "bg-white text-neutral-900 border border-neutral-300 hover:bg-neutral-50 " +
        "active:bg-neutral-100",
    ghost:
        "bg-transparent text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200",
    danger:
        "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 " +
        "shadow-[0_1px_2px_rgb(0_0_0_/_0.08)]",
    "danger-ghost":
        "bg-transparent text-red-700 hover:bg-red-50 active:bg-red-100",
};

const SIZE_CLASS = {
    sm: "h-8 px-3 text-xs gap-1.5 rounded-md",
    md: "h-10 px-4 text-sm gap-2 rounded-md",
    lg: "h-12 px-5 text-base gap-2 rounded-md",
    icon: "h-10 w-10 p-0 rounded-md",
};

const Button = forwardRef(function Button(
    {
        children, variant = "primary", size = "md", className = "",
        leftIcon: LeftIcon, rightIcon: RightIcon,
        loading = false, disabled = false, testId,
        type = "button", ...rest
    },
    ref,
) {
    const cls = [
        "inline-flex items-center justify-center font-semibold whitespace-nowrap",
        "transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed",
        "focus-visible:outline-2 focus-visible:outline-neutral-900 focus-visible:outline-offset-2",
        VARIANT_CLASS[variant] || VARIANT_CLASS.primary,
        SIZE_CLASS[size] || SIZE_CLASS.md,
        className,
    ].join(" ");
    return (
        <button
            ref={ref}
            type={type}
            data-testid={testId}
            disabled={disabled || loading}
            className={cls}
            {...rest}
        >
            {loading && (
                <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
            )}
            {!loading && LeftIcon && <LeftIcon className="h-4 w-4 shrink-0" />}
            {size !== "icon" && children}
            {!loading && RightIcon && <RightIcon className="h-4 w-4 shrink-0" />}
        </button>
    );
});

export default Button;
